// The Director URL model, in one place: which screen a location shows, and the href for a screen.
// Every navigation in the feature is built by `directorHref`, so the scope and company filter ride
// along from view to view; `parseDirectorLocation` is the only reader, so what a URL may ask for
// (an `all` scope, a Setup tab the caller may not see) is decided once, against `me`.
//
//   /director                      Inbox (or `?view=tasks|today|team|recurring|setup|new`)
//   /director/people/:personId     one person's tasks
//   /director/:id                  task detail
//   ?scope=all                     site admins (their sites) and super admins (every site)
//   ?company=<id>                  company filter (absent = all companies)
//   ?person=<id>&status=done|all   the tasks tab's filters, only with `view=tasks&scope=all`
//                                  (absent = anyone, open tasks)
import type { DirectorMe, DirectorScope } from '../../../../../modules/erp';

export const DIRECTOR_VIEWS = [
  'inbox',
  'tasks',
  'today',
  'team',
  'recurring',
  'setup',
  'new',
] as const;
export type DirectorView = (typeof DIRECTOR_VIEWS)[number];

export type DirectorTarget =
  | { kind: 'view'; view: DirectorView }
  | { kind: 'person'; personId: string }
  | { kind: 'task'; taskId: string };

/** Which tasks the tasks tab lists in `scope=all`: open (the default), closed, or every status. */
export const TASK_STATUS_FILTERS = ['open', 'done', 'all'] as const;
export type TaskStatusFilter = (typeof TASK_STATUS_FILTERS)[number];

/** The tasks tab's own filters in `scope=all`. */
export interface TaskListFilters {
  /** `null` = anyone. */
  personId: string | null;
  status: TaskStatusFilter;
}

export const NO_TASK_FILTERS: TaskListFilters = {
  personId: null,
  status: 'open',
};

export interface DirectorFilters {
  scope: DirectorScope;
  /** `null` = all companies. */
  companyId: string | null;
  /** The tasks tab's filters, only in `scope=all`; absent = `NO_TASK_FILTERS`. */
  taskFilters?: TaskListFilters;
}

export interface DirectorLocation extends DirectorFilters {
  target: DirectorTarget;
  /** Desktop `?view=new`: the Inbox with the new-task dialog opened over it. */
  newTaskDialog: boolean;
}

/** What the URL is checked against. */
export type DirectorCaller = Pick<
  DirectorMe,
  'reach' | 'canProvision' | 'companies'
>;

const BASE = '/director';
const PEOPLE = 'people';
const INBOX: DirectorTarget = { kind: 'view', view: 'inbox' };

function isView(value: string | null): value is DirectorView {
  return (DIRECTOR_VIEWS as readonly string[]).includes(value ?? '');
}

function decode(segment: string): string | null {
  try {
    const value = decodeURIComponent(segment);
    return value ? value : null;
  } catch {
    return null;
  }
}

function pathTarget(pathname: string): DirectorTarget | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== BASE.slice(1)) return null;
  const rest = segments.slice(1);
  if (rest.length === 2 && rest[0] === PEOPLE) {
    const personId = decode(rest[1]);
    return personId ? { kind: 'person', personId } : null;
  }
  if (rest.length === 1 && rest[0] !== PEOPLE) {
    const taskId = decode(rest[0]);
    return taskId ? { kind: 'task', taskId } : null;
  }
  return null;
}

/** Whether the caller may ask for `scope=all`: anyone whose reach goes beyond their own tasks (a
 *  site admin over their sites, a super admin over every site). The server refuses anyone else. */
export function mayUseAllScope(me: Pick<DirectorMe, 'reach'>): boolean {
  return me.reach !== 'own';
}

/** What `scope=all` covers for this caller, named once for the switch and the screen hints. */
export type AllScopeName = 'everyone' | 'site' | 'sites';

export function allScopeName(
  me: Pick<DirectorMe, 'reach' | 'sites'>
): AllScopeName {
  if (me.reach === 'all') return 'everyone';
  return me.sites.length > 1 ? 'sites' : 'site';
}

/** Whether the tasks tab's filters apply to this screen: the tasks tab listing a reach. */
function takesTaskFilters(target: DirectorTarget, scope: DirectorScope) {
  return target.kind === 'view' && target.view === 'tasks' && scope === 'all';
}

function isStatusFilter(value: string | null): value is TaskStatusFilter {
  return (TASK_STATUS_FILTERS as readonly string[]).includes(value ?? '');
}

function urlTarget(
  pathname: string,
  params: URLSearchParams,
  me: DirectorCaller,
  edition: { mobile: boolean }
): { target: DirectorTarget; newTaskDialog: boolean } {
  const fromPath = pathTarget(pathname);
  if (fromPath) return { target: fromPath, newTaskDialog: false };
  const view = params.get('view');
  if (!isView(view) || (view === 'setup' && !me.canProvision))
    return { target: INBOX, newTaskDialog: false };
  if (view === 'new' && !edition.mobile)
    return { target: INBOX, newTaskDialog: true };
  return { target: { kind: 'view', view }, newTaskDialog: false };
}

export function parseDirectorLocation(
  pathname: string,
  search: string,
  me: DirectorCaller,
  edition: { mobile: boolean }
): DirectorLocation {
  const params = new URLSearchParams(search);
  const { target, newTaskDialog } = urlTarget(pathname, params, me, edition);
  const scope: DirectorScope =
    params.get('scope') === 'all' && mayUseAllScope(me) ? 'all' : 'mine';
  const company = params.get('company');
  const companyId =
    company && me.companies.some(c => c.id === company) ? company : null;
  const location: DirectorLocation = {
    target,
    scope,
    companyId,
    newTaskDialog,
  };
  const personId = params.get('person') || null;
  const status = params.get('status');
  if (
    takesTaskFilters(target, scope) &&
    (personId !== null || isStatusFilter(status))
  )
    location.taskFilters = {
      personId,
      status: isStatusFilter(status) ? status : 'open',
    };
  return location;
}

export function directorHref(
  target: DirectorTarget,
  filters: DirectorFilters
): string {
  const params = new URLSearchParams();
  let path = BASE;
  if (target.kind === 'person')
    path = `${BASE}/${PEOPLE}/${encodeURIComponent(target.personId)}`;
  else if (target.kind === 'task')
    path = `${BASE}/${encodeURIComponent(target.taskId)}`;
  else if (target.view !== 'inbox') params.set('view', target.view);
  if (filters.scope === 'all') params.set('scope', 'all');
  if (filters.companyId) params.set('company', filters.companyId);
  const tasks = filters.taskFilters;
  if (tasks && takesTaskFilters(target, filters.scope)) {
    if (tasks.personId) params.set('person', tasks.personId);
    if (tasks.status !== 'open') params.set('status', tasks.status);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** A stable identity for the screen a target shows (used to key the mounted view). Every task shares
 *  one: the task detail stays mounted, keeping its master list, while it moves between tasks. */
export function targetKey(target: DirectorTarget): string {
  switch (target.kind) {
    case 'view':
      return `view:${target.view}`;
    case 'person':
      return `person:${target.personId}`;
    case 'task':
      return 'task';
  }
}
